import { Component, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, ApiHealthBadge, ModuleRouteGuard, PluginShell, type ShellNavItem } from '@striker/ui-shell';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { apiFetch } from './auth/session';
import { LoginPage } from './pages/LoginPage';
import { MasterEmailPage } from './pages/MasterEmailPage';
import { MasterEncryptionPage } from './pages/MasterEncryptionPage';
import { MasterHomePage } from './pages/MasterHomePage';
import { MasterModulesPage } from './pages/MasterModulesPage';
import { TenantStorePage } from './pages/TenantStorePage';
import { V3AgendaPage } from './pages/V3AgendaPage';
import { V3GymPage } from './pages/V3GymPage';
import { V3LoyaltyPage } from './pages/V3LoyaltyPage';
import { V3PresencePage } from './pages/V3PresencePage';
import { V3MarketingPage } from './pages/V3MarketingPage';
import { V3OrdersPage } from './pages/V3OrdersPage';
import { V3InventoryPage } from './pages/V3InventoryPage';
import { V3RecordsPage } from './pages/V3RecordsPage';
import { V3ProgramsPage } from './pages/V3ProgramsPage';
import { TenantModulesPage } from './pages/TenantModulesPage';
import { MasterNewTenantPage } from './pages/MasterNewTenantPage';
import { MasterPlansPage } from './pages/MasterPlansPage';
import { MasterResetPage } from './pages/MasterResetPage';
import { MasterBillingRegistryPage } from './pages/MasterBillingRegistryPage';
import { MasterSmsPacksPage } from './pages/MasterSmsPacksPage';
import { MasterPortaleSbloccoPage } from './pages/MasterPortaleSbloccoPage';
import { MasterPortaleCardsPage } from './pages/MasterPortaleCardsPage';
import { MasterPortaleScraperPage } from './pages/MasterPortaleScraperPage';
import { MasterPortaleGeographyPage } from './pages/MasterPortaleGeographyPage';
import { MasterSosPage } from './pages/MasterSosPage';
import { MasterSupportInboxPage } from './pages/MasterSupportInboxPage';
import { MasterTenantsPage } from './pages/MasterTenantsPage';
import { MasterEndUsersPage } from './pages/MasterEndUsersPage';
import { MasterReviewsPage } from './pages/MasterReviewsPage';
import { EndUserDashboardPage } from './pages/EndUserDashboardPage';
import { EndUserRegisterPage } from './pages/EndUserRegisterPage';
import { EndUserPreferencesPage } from './pages/EndUserPreferencesPage';
import { CategoryMigrationPage } from './pages/CategoryMigrationPage';
import { MasterTwilioPage } from './pages/MasterTwilioPage';
import { MasterAuditPage } from './pages/MasterAuditPage';
import { MasterNotificationsPage } from './pages/MasterNotificationsPage';
import { PublicSosPage } from './pages/PublicSosPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { RegisterPage } from './pages/RegisterPage';
import { JoinPage } from './pages/JoinPage';
import { WelcomePage } from './pages/WelcomePage';
import { OnboardingBanner } from './components/OnboardingBanner';
import {
  BillingBlockedBanner,
  BillingCancelPage,
  BillingReturnRedirect,
  BillingSuccessPage,
  TenantBillingPage,
} from './pages/TenantBillingPage';
import { TenantAnalyticsPage } from './pages/TenantAnalyticsPage';
import { TenantDirectoryPage } from './pages/TenantDirectoryPage';
import { ThemeProvider } from './theme/ThemeProvider';
import { PLUGIN_UI_ROUTES, PLUGIN_PUBLIC_ROUTES, PLUGIN_NAV_GROUPS, PLUGIN_LANDING_PATHS } from './pluginUiRegistry.generated';
import { IncomingReservationAlert } from './pluginShellComponents';
import { buildNavItemsFromRegistry } from './buildNavItems';

// Normalize plugin route paths: strip leading slash so /tenant/${path} doesn't
// produce double slashes (e.g. /tenant//hello when path is "/hello").
const tenantRoutePath = (p: string) => `/tenant/${p.replace(/^\/+/, '')}`;

// â”€â”€ Lazy imports for components used OUTSIDE the dynamic plugin routing â”€â”€
// Plugin tenant pages are loaded dynamically via PLUGIN_UI_ROUTES (no hardcoded if/else).
// These remain because they are used in public routes or as embedded components.
// The public landing page is resolved from the generated registry — zero direct
// plugin-package imports here. Shell-level widgets live in pluginShellComponents.ts.
const PortalePage = PLUGIN_UI_ROUTES.find(
  (r) => r.pluginId === 'portale' && r.path === '/portale',
)?.Component;

// âââ─â─ Tenant landing resolver âââ─â─
// Resolves the default landing path for a logged-in tenant user by
// looking up the first active base module in PLUGIN_LANDING_PATHS.
type ModuleCheck = (code: string) => boolean;
function resolveTenantLanding(
  user: { role: 'platform_admin' | 'tenant_user' | 'end_user'; storeId: number | null },
  moduleOn: ModuleCheck,
): string {
  if (user.role === 'platform_admin' && !user.storeId) return '/master';
  if (user.role === 'end_user') return '/end-user';
  for (const lp of PLUGIN_LANDING_PATHS) {
    if (moduleOn(lp.baseModule)) {
      return `/tenant/${lp.landingRoute.replace(/^\/+/, '')}`;
    }
  }
  return '/tenant/store';
}

function AuthenticatedApp() {
  const { ready, user, entitlements, logout } = useAuth();
  const location = useLocation();
  const [billingLocked, setBillingLocked] = useState(false);
  const [billingReason, setBillingReason] = useState<string | null>(null);
  const [sosOpenCount, setSosOpenCount] = useState(0);
  const [apiOnline, setApiOnline] = useState<boolean | null>(null);

  const enabledModules = entitlements?.modules || [];
  const enforcementMode = entitlements?.enforcement_mode || 'enforce';
  const moduleOn = (code: string) => {
    if (enforcementMode === 'off') return true;
    return enabledModules.includes(code.toLowerCase());
  };

  // Fetch SOS open count for sidebar badge (master only)
  useEffect(() => {
    if (user?.role !== 'platform_admin') return;
    let cancelled = false;
    async function fetchSos() {
      try {
        const res = await apiFetch('/api/v1/master/support/sos-tickets/open-count');
        if (cancelled || !res.ok) return;
        const body = (await res.json().catch(() => null)) as { open_count?: number } | null;
        if (body && !cancelled) setSosOpenCount(Number(body.open_count || 0));
      } catch { /* ignore */ }
    }
    void fetchSos();
    const interval = setInterval(fetchSos, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user?.role]);

  // API health check
  useEffect(() => {
    let cancelled = false;
    async function checkHealth() {
      try {
        const res = await fetch('/health');
        if (cancelled) return;
        setApiOnline(res.ok);
      } catch {
        if (!cancelled) setApiOnline(false);
      }
    }
    void checkHealth();
    const interval = setInterval(checkHealth, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (!user?.storeId) {
      setBillingLocked(false);
      setBillingReason(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch('/api/v1/tenant/billing/status');
        if (!res.ok || cancelled) return;
        const body = (await res.json().catch(() => null)) as { locked?: boolean; reason?: string | null } | null;
        if (!body) return;
        if (cancelled) return;
        setBillingLocked(Boolean(body.locked));
        setBillingReason(body.reason || null);
      } catch {
        /* ignore banner errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.storeId]);

  const navItems = useMemo((): ShellNavItem[] => {
    const canUseTenantPlugins = Boolean(user?.storeId);
    const byPath = new Map(
      PLUGIN_UI_ROUTES.filter((r) => r.navItem || r.path).map((r) => [tenantRoutePath(r.path), r]),
    );
    const hasRoute = (path: string) => byPath.has(path);

    // Resolve tenant category: explicit from entitlements, else infer from
    // enabled base modules (matches tenantLandingPath fallback logic).
    const tenantCategory =
      entitlements?.category ??
      (moduleOn('restaurant-base') || moduleOn('restaurant-reservations')
        ? 'restaurant'
        : moduleOn('beauty-base') || moduleOn('beauty-bookings')
          ? 'beauty'
          : moduleOn('gym-base')
            ? 'gym'
            : moduleOn('hair-base') || moduleOn('hair-booking')
              ? 'hair'
              : null);

    // Map module code prefix â†’ vertical category.
    // haccp is food-safety â†’ restaurant vertical; support is cross-vertical.
    const MODULE_CATEGORY: Record<string, string> = {
      restaurant: 'restaurant',
      beauty: 'beauty',
      gym: 'gym',
      hair: 'hair',
      haccp: 'restaurant',
      support: '__cross',
      portale: '__cross',
    };
    // Returns true if a module belongs to the tenant's vertical (or is cross-vertical).
    const fitsCategory = (moduleCode: string) => {
      const prefix = moduleCode.split('-')[0];
      const cat = MODULE_CATEGORY[prefix];
      if (!cat || cat === '__cross') return true; // unknown or cross-vertical â†’ always show
      if (!tenantCategory) {
        // No category detected â†’ only show if the module is actually enabled
        return moduleOn(moduleCode);
      }
      return cat === tenantCategory;
    };

    if (user?.role === 'platform_admin' && !canUseTenantPlugins) {
      return [
        { key: 'dashboard', label: 'Dashboard', path: '/master', icon: 'dashboard' },
        {
          key: 'tenants',
          label: 'Aziende',
          path: '/master/tenants',
          icon: 'tenants',
          children: [
            { key: 'tenants-list', label: 'Elenco', path: '/master/tenants', icon: 'list' },
            { key: 'tenants-new', label: 'Nuovo tenant', path: '/master/tenants/new', icon: 'plus' },
          ],
        },
        { key: 'end-users', label: 'End Users', path: '/master/end-users', icon: 'users' },
        { key: 'reviews', label: 'Recensioni', path: '/master/reviews', icon: 'star' },
        {
          key: 'catalog',
          label: 'Catalogo',
          path: '/master/plans',
          icon: 'catalog',
          children: [
            { key: 'plans', label: 'Piani', path: '/master/plans', icon: 'plans' },
            { key: 'modules', label: 'Moduli', path: '/master/modules', icon: 'modules' },
            { key: 'sms-packs', label: 'Pacchetti SMS', path: '/master/sms-packs', icon: 'sms' },
          ],
        },
        { key: 'billing-registry', label: 'Billing ops', path: '/master/billing', icon: 'billing' },
        {
          key: 'support',
          label: 'Supporto',
          path: '/master/support',
          icon: 'support',
          children: [
            { key: 'support-inbox', label: 'Inbox tenant', path: '/master/support', icon: 'inbox' },
            { key: 'support-sos', label: 'SOS emergenze', path: '/master/support/sos', icon: 'alert', badgeCount: sosOpenCount, badgeTone: 'error' },
          ],
        },
        {
          key: 'portale',
          label: 'Portale',
          path: '/master/portale/sblocco',
          icon: 'portale',
          children: [
            { key: 'portale-sblocco', label: 'Sblocco', path: '/master/portale/sblocco' },
            { key: 'portale-cards', label: 'CMS Card', path: '/master/portale/cards' },
            { key: 'portale-scraper', label: 'Lead scraper', path: '/master/portale/scraper' },
            { key: 'portale-geography', label: 'Geografia & Categorie', path: '/master/portale/geography' },
          ],
        },
        {
          key: 'config',
          label: 'Configurazione',
          path: '/master/email',
          icon: 'config',
          children: [
            { key: 'config-email', label: 'Email', path: '/master/email' },
            { key: 'config-twilio', label: 'Twilio', path: '/master/twilio' },
            { key: 'config-encryption', label: 'Encryption', path: '/master/encryption' },
            { key: 'config-reset', label: 'Reset', path: '/master/reset' },
          ],
        },
        { key: 'audit', label: 'Audit log', path: '/master/audit', icon: 'audit' },
        { key: 'notifications', label: 'Notifiche', path: '/master/notifications', icon: 'alert' },
      ];
    }

    if (user?.role === 'end_user') {
      return [
        { key: 'end-user-dashboard', label: 'Il mio Passaporto', path: '/end-user' },
        { key: 'end-user-preferences', label: 'Preferenze', path: '/end-user/preferences' },
      ];
    }

    // Build plugin nav items dynamically from the UI registry
    const pluginItems = buildNavItemsFromRegistry({
      routes: PLUGIN_UI_ROUTES,
      navGroups: PLUGIN_NAV_GROUPS,
      canUseTenantPlugins,
      moduleOn,
      fitsCategory,
      hasRoute,
    });

    const items: ShellNavItem[] = [...pluginItems];

    // Platform-managed items (not plugin-owned — always hardcoded)
    if (canUseTenantPlugins) {
      items.push({ key: 'billing', label: 'Fatturazione', path: '/tenant/billing', icon: 'billing' });
    }
    if (user?.role === 'platform_admin') {
      items.push({ key: 'master', label: 'Master', path: '/master' });
    }
    if (canUseTenantPlugins) {
      items.push({ key: 'store', label: 'Store Moduli', path: '/tenant/store' });
      items.push({ key: 'my-modules', label: 'I miei moduli', path: '/tenant/modules' });
    }

    return items;
  }, [enabledModules, enforcementMode, user, entitlements?.category]);

  if (!ready) {
    return (
      <div className="sk-login">
        <p className="sk-muted">Caricamento sessione…</p>
      </div>
    );
  }

  if (!user && location.pathname === '/') {
    return (
      <Suspense fallback={<p className="sk-muted">Caricamento Portale…</p>}>
        {PortalePage ? <PortalePage /> : <LoginPage />}
      </Suspense>
    );
  }
  if (!user) return <LoginPage />;

  // Logged-in user on /login or / — redirect to role-based landing
  if (location.pathname === '/login' || location.pathname === '/') {
    const landing = resolveTenantLanding(user, moduleOn);
    return <Navigate to={landing} replace />;
  }

  const consoleLabel =
    user.displayName || user.email || (user.storeId ? `Tenant ${user.storeId}` : 'Tenant');

  const tenantLandingPath = resolveTenantLanding(user, moduleOn);

  return (
    <AppShell
      brand="StrikerPos"
      consoleLabel={consoleLabel}
      navItems={navItems}
      homePath={tenantLandingPath}
      topbarRight={
        <div className="sk-shell__user">
          <span className="sk-shell__health">
            <ApiHealthBadge online={apiOnline} />
          </span>
          <p className="sk-shell__user-meta">
            {user.email || user.displayName || user.role}
            {entitlements?.plan_code ? ` Â· ${entitlements.plan_code}` : ''}
          </p>
          <button type="button" className="sk-shell__logout" onClick={logout}>
            Esci
          </button>
        </div>
      }
    >
      <BillingBlockedBanner locked={billingLocked} reason={billingReason} />
      {user.storeId ? <OnboardingBanner /> : null}
      {user.storeId && (moduleOn('restaurant-base') || moduleOn('restaurant-reservations')) ? (
        <Suspense fallback={null}>
          <IncomingReservationAlert storeId={user.storeId} enabled />
        </Suspense>
      ) : null}
      <Routes>
        <Route path="/marketing" element={<Navigate to="/register" replace />} />
        <Route path="/iscriviti" element={<JoinPage />} />
        <Route
          path="/"
          element={
            user ? (
              <Navigate to={tenantLandingPath} replace />
            ) : (
              <Suspense fallback={<p className="sk-muted">Caricamento Portale…</p>}>
                {PortalePage ? <PortalePage /> : <LoginPage />}
              </Suspense>
            )
          }
        />
        <Route path="/tenant" element={<Navigate to={tenantLandingPath} replace />} />
        <Route
          path="/tenant/directory"
          element={
            user.storeId ? (
              <TenantDirectoryPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/analytics"
          element={
            user.storeId ? (
              <TenantAnalyticsPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store per visualizzare analytics.</p>
            )
          }
        />
        <Route
          path="/tenant/billing"
          element={
            user.storeId ? (
              <TenantBillingPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store per gestire l’abbonamento.</p>
            )
          }
        />
        <Route
          path="/tenant/store"
          element={
            user.storeId ? (
              <TenantStorePage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store per accedere allo store.</p>
            )
          }
        />
        <Route
          path="/tenant/modules"
          element={
            user.storeId ? (
              <TenantModulesPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store per gestire i moduli.</p>
            )
          }
        />
        <Route
          path="/tenant/agenda-v3"
          element={
            user.storeId ? (
              <V3AgendaPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/gym-v3"
          element={
            user.storeId ? (
              <V3GymPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/loyalty-v3"
          element={
            user.storeId ? (
              <V3LoyaltyPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/presence-v3"
          element={
            user.storeId ? (
              <V3PresencePage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/marketing-v3"
          element={
            user.storeId ? (
              <V3MarketingPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/orders-v3"
          element={
            user.storeId ? (
              <V3OrdersPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/inventory-v3"
          element={
            user.storeId ? (
              <V3InventoryPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/records-v3"
          element={
            user.storeId ? (
              <V3RecordsPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/tenant/programs-v3"
          element={
            user.storeId ? (
              <V3ProgramsPage />
            ) : (
              <p className="sk-muted">Serve un utente tenant con store.</p>
            )
          }
        />
        <Route
          path="/master"
          element={
            user.role === 'platform_admin' ? <MasterHomePage /> : <Navigate to="/tenant" replace />
          }
        />
        <Route path="/master/theme" element={<Navigate to="/master" replace />} />
        <Route
          path="/master/tenants"
          element={
            user.role === 'platform_admin' ? (
              <MasterTenantsPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/tenants/new"
          element={
            user.role === 'platform_admin' ? (
              <MasterNewTenantPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/end-users"
          element={
            user.role === 'platform_admin' ? (
              <MasterEndUsersPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/reviews"
          element={
            user.role === 'platform_admin' ? (
              <MasterReviewsPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/end-user"
          element={
            user.role === 'end_user' ? (
              <EndUserDashboardPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/end-user/preferences"
          element={
            user.role === 'end_user' ? (
              <EndUserPreferencesPage />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/tenant/category-migration"
          element={
            user.role === 'tenant_user' ? (
              <CategoryMigrationPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/plans"
          element={
            user.role === 'platform_admin' ? <MasterPlansPage /> : <Navigate to="/tenant" replace />
          }
        />
        <Route
          path="/master/modules"
          element={
            user.role === 'platform_admin' ? (
              <MasterModulesPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/encryption"
          element={
            user.role === 'platform_admin' ? (
              <MasterEncryptionPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/billing"
          element={
            user.role === 'platform_admin' ? (
              <MasterBillingRegistryPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/sms-packs"
          element={
            user.role === 'platform_admin' ? (
              <MasterSmsPacksPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/reset"
          element={
            user.role === 'platform_admin' ? <MasterResetPage /> : <Navigate to="/tenant" replace />
          }
        />
        <Route
          path="/master/email"
          element={
            user.role === 'platform_admin' ? (
              <MasterEmailPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/twilio"
          element={
            user.role === 'platform_admin' ? (
              <MasterTwilioPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/support"
          element={
            user.role === 'platform_admin' ? (
              <MasterSupportInboxPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/support/sos"
          element={
            user.role === 'platform_admin' ? (
              <MasterSosPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/portale/riscatti"
          element={<Navigate to="/master/portale/sblocco" replace />}
        />
        <Route
          path="/master/portale/sblocco"
          element={
            user.role === 'platform_admin' ? (
              <MasterPortaleSbloccoPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/portale/cards"
          element={
            user.role === 'platform_admin' ? (
              <MasterPortaleCardsPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/portale/scraper"
          element={
            user.role === 'platform_admin' ? (
              <MasterPortaleScraperPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/portale/geography"
          element={
            user.role === 'platform_admin' ? (
              <MasterPortaleGeographyPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/audit"
          element={
            user.role === 'platform_admin' ? (
              <MasterAuditPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        <Route
          path="/master/notifications"
          element={
            user.role === 'platform_admin' ? (
              <MasterNotificationsPage />
            ) : (
              <Navigate to="/tenant" replace />
            )
          }
        />
        {PLUGIN_UI_ROUTES.map((route) => (
          <Route
            key={`${route.pluginId}-${route.path}`}
            path={tenantRoutePath(route.path)}
            element={
              user.storeId ? (
                <ModuleRouteGuard
                  moduleCode={route.moduleCode}
                  enabledModules={enabledModules}
                  enforcementMode={enforcementMode}
                >
                  <PluginShell>
                    <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
                      <route.Component />
                    </Suspense>
                  </PluginShell>
                </ModuleRouteGuard>
              ) : (
                <p className="sk-muted">
                  Serve un utente tenant con store (es. owner@local.test). Il master non ha scope
                  sala/tavoli.
                </p>
              )
            }
          />
        ))}
      </Routes>
    </AppShell>
  );
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('App render error:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'monospace', fontSize: '0.9rem' }}>
          <h2 style={{ color: '#dc2626' }}>Runtime Error</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{this.state.error.message}</pre>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#6b7280', marginTop: '1rem' }}>{this.state.error.stack}</pre>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <Routes>
        {PLUGIN_PUBLIC_ROUTES.map((route) => (
          <Route
            key={`${route.pluginId}-${route.pattern}`}
            path={route.pattern}
            element={
              <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
                <route.Component />
              </Suspense>
            }
          />
        ))}
        <Route path="/support/sos" element={<PublicSosPage />} />
        <Route
          path="/forgot-password"
          element={
            <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
              <RegisterPage />
            </Suspense>
          }
        />
        <Route
          path="/end-user/register"
          element={<EndUserRegisterPage />}
        />
        <Route
          path="/welcome"
          element={
            <Suspense fallback={<p className="sk-muted">Caricamento…</p>}>
              <WelcomePage />
            </Suspense>
          }
        />
        {/* Billing redirect routes — public so Stripe can redirect unauthenticated users */}
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="/billing/return" element={<BillingReturnRedirect />} />
        <Route
          path="*"
          element={
            <AuthProvider>
              <AuthenticatedApp />
            </AuthProvider>
          }
        />
      </Routes>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

